<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface TimelineSubEvent {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  color?: string;
  tooltipContent?: string;
}

interface TimelineEvent {
  id: string;
  title: string;
  events: TimelineSubEvent[];
}

const props = defineProps<{
  events: TimelineEvent[];
  callBack?: (id: string) => void;
}>();

const { locale } = useI18n();
const categories = computed(() => props.events.map((e) => e.title));

interface FlatBar {
  categoryIndex: number;
  eventId: string;
  subEventId: string;
  title: string;
  name: string;
  start: number;
  end: number;
  color: string;
  tooltip?: string;
}

const flatBars = computed<FlatBar[]>(() =>
  props.events.flatMap((event, categoryIndex) =>
    event.events.map((sub) => ({
      categoryIndex,
      eventId: event.id,
      subEventId: sub.id,
      title: event.title,
      name: sub.name,
      start: new Date(sub.startDate).getTime(),
      end: new Date(sub.endDate).getTime(),
      color: sub.color || "#4F7DF3",
      tooltip: sub.tooltipContent,
    })),
  ),
);

const seriesData = computed(() =>
  flatBars.value.map((bar) => ({
    name: bar.name,
    value: [bar.categoryIndex, bar.start, bar.end],
    itemStyle: { color: bar.color },
    eventId: bar.eventId,
    subEventId: bar.subEventId,
    label: bar.name,
    tooltip: bar.tooltip,
  })),
);

function renderItem(params: any, api: any) {
  const categoryIndex = api.value(0);
  const start = api.coord([api.value(1), categoryIndex]);
  const end = api.coord([api.value(2), categoryIndex]);
  const height = api.size([0, 1])[1] * 0.6;
  const width = Math.max(end[0] - start[0], 2);

  const rectShape = {
    x: start[0],
    y: start[1] - height / 2,
    width,
    height,
    r: 6,
  };

  const barName = seriesData.value[params.dataIndex]?.name ?? "";

  return {
    type: "group",
    children: [
      {
        type: "rect",
        shape: rectShape,
        style: api.style(),
      },
      {
        type: "text",
        style: {
          x: rectShape.x + width / 2,
          y: rectShape.y + height / 2,
          text: barName,
          fill: "#fff",
          fontSize: 11,
          fontWeight: "bold",
          align: "center",
          verticalAlign: "middle",
          // hide the label if the bar is too narrow to fit it
          overflow: width < 30 ? "none" : "truncate",
          width: width < 30 ? 0 : width - 8,
          opacity: width < 30 ? 0 : 1,
        },
      },
    ],
  };
}

function handleChartClick(params: any) {
  const bar = seriesData.value[params.dataIndex];
  if (!bar) return;
  props.callBack?.(bar.eventId);
}

const option = computed(() => ({
  tooltip: {
    formatter: (params: any) => {
      const bar = flatBars.value[params.dataIndex];
      if (!bar) return "";
      const startLabel = new Date(bar.start).toLocaleString(locale.value);
      const endLabel = new Date(bar.end).toLocaleString(locale.value);
      console.log("bar", bar.tooltip);
      return (
        bar.tooltip ??
        `<strong>${bar.name}</strong><br/>${startLabel} → ${endLabel}`
      );
    },
  },
  grid: {
    left: 10,
    right: 20,
    top: 40,
    bottom: 20,
    containLabel: true,
  },
  xAxis: {
    type: "time",
    position: "top",
    axisLine: { show: false },
    splitLine: { show: true, lineStyle: { color: "#f0f0f0" } },
    axisLabel: {
      showMinLabel: true,
      showMaxLabel: true,
      formatter: (value: number) => {
        const date = new Date(value);
        const day = date.getDate();
        const month = date.toLocaleString(locale.value, { month: "short" });
        return `${day} ${month}`;
      },
    },
  },
  yAxis: {
    type: "category",
    data: categories.value,
    axisLine: { show: false },
    axisTick: { show: false },
  },
  series: [
    {
      type: "custom",
      renderItem,
      itemStyle: { opacity: 0.9 },
      encode: {
        x: [1, 2],
        y: 0,
      },
      data: seriesData.value,
      cursor: "pointer",
    },
  ],
}));
</script>

<template>
  <VChart
    :option="option"
    class="w-full"
    :style="{ height: `${events.length * 50 + 60}px` }"
    autoresize
    @click="handleChartClick"
  />
</template>
